/**
 * 文档搜索 Hook
 * 提供文档搜索、导入和高级过滤功能
 */
import { useState, useCallback, useEffect } from 'react'
import { Document, DocumentFilter } from '../../../types/document'
import { useAppStore } from '../../../store/useAppStore'

/**
 * 搜索状态
 */
export interface DocumentSearchState {
  query: string
  results: Document[]
  isSearching: boolean
  error: string | null
  searchHistory: string[]
  filters: DocumentFilter
  filteredResults: Document[]
}

/**
 * 搜索操作
 */
export interface DocumentSearchActions {
  setQuery: (query: string) => void
  search: (query?: string) => Promise<Document[]>
  clearSearch: () => void
  setFilters: (filters: Partial<DocumentFilter>) => void
  clearFilters: () => void
  addToHistory: (query: string) => void
  removeFromHistory: (query: string) => void
  clearHistory: () => void
}

/**
 * 文档搜索 Hook
 */
export function useDocumentSearch(): DocumentSearchState & DocumentSearchActions {
  const { documents } = useAppStore()
  
  const [state, setState] = useState<DocumentSearchState>({
    query: '',
    results: [],
    isSearching: false,
    error: null,
    searchHistory: [],
    filters: {
      query: '',
      sortBy: 'updated',
      sortOrder: 'desc'
    },
    filteredResults: []
  })

  /**
   * 更新状态
   */
  const updateState = useCallback((updates: Partial<DocumentSearchState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  /**
   * 设置搜索查询
   */
  const setQuery = useCallback((query: string) => {
    updateState({ query })
    
    // 同时更新过滤器中的查询
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        query
      }
    }))
  }, [updateState])

  /**
   * 执行搜索
   */
  const search = useCallback(async (searchQuery?: string): Promise<Document[]> => {
    const queryToSearch = searchQuery ?? state.query
    
    updateState({ isSearching: true, error: null })
    
    try {
      // 如果查询为空，返回所有文档
      if (!queryToSearch.trim()) {
        updateState({
          results: documents,
          isSearching: false,
          filteredResults: documents
        })
        return documents
      }

      // 执行搜索
      const lowercaseQuery = queryToSearch.toLowerCase()
      const filteredDocuments = documents.filter(doc =>
        doc.title.toLowerCase().includes(lowercaseQuery) ||
        doc.content.toLowerCase().includes(lowercaseQuery)
      )

      // 按更新时间降序排列
      const sortedResults = filteredDocuments.sort((a, b) => b.updated - a.updated)

      updateState({
        results: sortedResults,
        isSearching: false,
        filteredResults: sortedResults
      })

      // 添加到搜索历史
      if (queryToSearch.trim()) {
        addToHistory(queryToSearch.trim())
      }

      return sortedResults
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '搜索失败'
      updateState({
        error: errorMessage,
        isSearching: false,
        results: [],
        filteredResults: []
      })
      return []
    }
  }, [state.query, documents, updateState])

  /**
   * 清除搜索
   */
  const clearSearch = useCallback(() => {
    updateState({
      query: '',
      results: documents,
      isSearching: false,
      error: null,
      filters: {
        query: '',
        sortBy: 'updated',
        sortOrder: 'desc'
      },
      filteredResults: documents
    })
  }, [documents, updateState])

  /**
   * 设置过滤器
   */
  const setFilters = useCallback((newFilters: Partial<DocumentFilter>) => {
    setState(prev => {
      const updatedFilters = { ...prev.filters, ...newFilters }
      
      // 应用过滤器
      let filteredDocs = prev.query 
        ? prev.results 
        : documents

      // 应用排序
      if (updatedFilters.sortBy) {
        const sortBy = updatedFilters.sortBy
        const sortOrder = updatedFilters.sortOrder || 'desc'
        
        filteredDocs = [...filteredDocs].sort((a, b) => {
          const aValue = a[sortBy]
          const bValue = b[sortBy]
          
          if (sortOrder === 'asc') {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
          } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
          }
        })
      }

      return {
        ...prev,
        filters: updatedFilters,
        filteredResults: filteredDocs
      }
    })
  }, [documents])

  /**
   * 清除过滤器
   */
  const clearFilters = useCallback(() => {
    updateState({
      filters: {
        query: '',
        sortBy: 'updated',
        sortOrder: 'desc'
      },
      filteredResults: state.query ? state.results : documents
    })
  }, [state.query, state.results, documents, updateState])

  /**
   * 添加到搜索历史
   */
  const addToHistory = useCallback((query: string) => {
    setState(prev => {
      // 避免重复
      const filteredHistory = prev.searchHistory.filter(item => item !== query)
      const newHistory = [query, ...filteredHistory].slice(0, 10) // 保留最近10条
      
      return {
        ...prev,
        searchHistory: newHistory
      }
    })
  }, [])

  /**
   * 从搜索历史中移除
   */
  const removeFromHistory = useCallback((query: string) => {
    setState(prev => ({
      ...prev,
      searchHistory: prev.searchHistory.filter(item => item !== query)
    }))
  }, [])

  /**
   * 清除搜索历史
   */
  const clearHistory = useCallback(() => {
    updateState({ searchHistory: [] })
  }, [updateState])

  /**
   * 当文档列表变化时更新搜索结果
   */
  useEffect(() => {
    if (state.query.trim()) {
      search(state.query)
    } else {
      updateState({
        results: documents,
        filteredResults: documents
      })
    }
  }, [documents]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * 监听过滤器变化，重新应用过滤
   */
  useEffect(() => {
    if (state.filters.query !== state.query) {
      setFilters({ query: state.query })
    }
  }, [state.query, state.filters.query, setFilters])

  return {
    ...state,
    setQuery,
    search,
    clearSearch,
    setFilters,
    clearFilters,
    addToHistory,
    removeFromHistory,
    clearHistory
  }
}